import { useState, useEffect, useRef } from 'react';
import { mastersAPI } from '../utils/api';

/**
 * frontend/src/hooks/useMasters.js
 *
 * Dropdown options, from Masters instead of a hardcoded array.
 *
 * Every list is fetched once per page load and cached for the session — a
 * picklist changes a few times a year, and re-fetching it on every form open
 * would put dozens of requests behind a single modal.
 *
 * FALLBACK MATTERS. If Masters is empty, unreachable, or the migration has
 * not run, the hook returns the list passed in as `fallback`. A form with an
 * empty status dropdown is unusable, so it degrades to today's behaviour
 * rather than to nothing.
 *
 *   const statuses = useMaster('task_status', ['Pending','In Progress','Completed']);
 *   {statuses.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
 *
 * Options come back as { value, label }. The VALUE is what the API stores and
 * what any CHECK constraint validates; the LABEL is only what the user reads,
 * which is why an Admin may change one and not the other.
 */

const cache = new Map();     // category -> [{value,label}]
const inflight = new Map();  // category -> Promise

/** Turn a plain array of strings into option objects. */
const asOptions = (list = []) =>
  list.map(v => (typeof v === 'string' ? { value: v, label: v } : v));

async function fetchCategory(category) {
  if (cache.has(category)) return cache.get(category);
  if (inflight.has(category)) return inflight.get(category);

  // The whole call is wrapped, not just the promise. Getting the method name
  // wrong throws a TypeError synchronously, which a .catch() on the promise
  // never sees — that took the entire app to a blank page, because almost
  // every form asks for a list. Any failure here must degrade to the
  // fallback, never propagate into a render.
  let request;
  try {
    // mastersAPI exposes getCategory; getAll(category) is the other option.
    request = mastersAPI.getCategory
      ? mastersAPI.getCategory(category)
      : mastersAPI.getAll(category);
  } catch {
    cache.set(category, []);
    return [];
  }
  if (!request || typeof request.then !== 'function') {
    cache.set(category, []);
    return [];
  }

  const p = request
    .then(r => {
      // getAll() groups by category; getCategory() returns a flat array.
      const raw = Array.isArray(r.data) ? r.data : (r.data?.[category] || []);
      const rows = raw
        .filter(x => x && x.is_active !== false)
        .map(x => ({ value: x.value, label: x.label || x.value }));
      cache.set(category, rows);
      return rows;
    })
    .catch(() => [])          // the caller falls back; never throw into a form
    .finally(() => inflight.delete(category));

  inflight.set(category, p);
  return p;
}

/**
 * One category.
 * @param {string} category  e.g. 'task_status'
 * @param {Array}  fallback  used when Masters has nothing for it
 */
export function useMaster(category, fallback = []) {
  // The fallback is written inline at the call site, so it is a new array on
  // every render. Keeping it in a ref lets the effect depend on the category
  // alone without going stale.
  const fallbackRef = useRef(fallback);
  fallbackRef.current = fallback;

  const [options, setOptions] = useState(() => cache.get(category) || asOptions(fallback));

  useEffect(() => {
    let cancelled = false;
    fetchCategory(category).then(rows => {
      if (cancelled) return;
      // An empty result means "not configured", not "no options" — keep the
      // fallback so the form still works.
      setOptions(rows.length ? rows : asOptions(fallbackRef.current));
    });
    return () => { cancelled = true; };
  }, [category]);

  return options;
}

/**
 * Several at once, for forms with many pickers.
 *   const m = useMasters({ status:['task_status',STATUSES], priority:['priority',PRIORITIES] });
 *   m.status -> [{value,label}]
 */
export function useMasters(spec) {
  const specRef = useRef(spec);
  specRef.current = spec;

  const keys = Object.keys(spec);
  const [maps, setMaps] = useState(() => {
    const init = {};
    for (const k of keys) {
      const [cat, fb] = spec[k];
      init[k] = cache.get(cat) || asOptions(fb);
    }
    return init;
  });

  // The spec is an object literal, so it is a new reference every render;
  // key off the category names instead or this loops forever.
  const signature = keys.map(k => spec[k][0]).join('|');

  useEffect(() => {
    let cancelled = false;
    const s = specRef.current;
    const ks = Object.keys(s);
    Promise.all(ks.map(k => fetchCategory(s[k][0]))).then(results => {
      if (cancelled) return;
      const next = {};
      ks.forEach((k, i) => {
        next[k] = results[i].length ? results[i] : asOptions(s[k][1]);
      });
      setMaps(next);
    });
    return () => { cancelled = true; };
  }, [signature]);

  return maps;
}

/** Drop the cache after an Admin edits Masters, so forms pick it up. */
export function clearMastersCache(category) {
  if (category) cache.delete(category);
  else cache.clear();
}
